import { Outlet, createBrowserRouter } from 'react-router-dom';

import {
    AuthorizedOutlet,
    AuthorizedRoute,
    LoginCallback,
    LogoutRoute,
} from '@myphx-engineering/auth/client';

import { FillPrescriptionProvider } from '../../contexts/fill/FillPrescriptionProvider';
import { PrescriberOrderProvider } from '../../contexts/PrescriberOrderProvider';
import { AutoDetectPathway } from '../fill/AutoDetectPathway';
import { EnhancedFillFlagCheck } from '../fill/EnhancedFillFlagCheck';
import AppRoot from '../layout/AppRoot';
import { Maintenance } from '../maintenance/Maintenance';
import { MedicineCabinetHistoryPage } from '../pages/cabinet/MedicineCabinetHistoryPage';
import { MedicineCabinetPage } from '../pages/cabinet/MedicineCabinetPage';
import { PrescriptionPage } from '../pages/cabinet/prescriptions/PrescriptionPage';
import { AttestationScreen } from '../pages/fill/AttestationScreen';
import { CoverageSelectScreen } from '../pages/fill/CoverageSelectScreen';
import { CoverageSubSelectScreen } from '../pages/fill/CoverageSubSelectScreen';
import { OnHoldScreen } from '../pages/fill/OnHoldScreen';
import { PathSelectionScreen } from '../pages/fill/PathSelectionScreen';
import { PharmacyPreviewScreen } from '../pages/fill/PharmacyPreviewScreen';
import { SelectPharmacyScreen } from '../pages/fill/SelectPharmacyScreen';
import { SwitchPrescriptionScreen } from '../pages/fill/SwitchPrescriptionScreen';
import { HomePage } from '../pages/home/HomePage';
import OrderConfirmationPage from '../pages/order-confirmation/OrderConfirmationPage';
import OrderDetailsPage from '../pages/order-details/OrderDetailsPage';
import OrderSummaryPage from '../pages/order-summary/OrderSummaryPage';
import { OtherOptionsPage } from '../pages/other-options/OtherOptionsPage';
import { ProfileConsentDetailPage } from '../pages/profile/consents/ProfileConsentDetailPage';
import { ProfileConsentsPage } from '../pages/profile/consents/ProfileConsentsPage';
import { ProfilePage } from '../pages/profile/ProfilePage';
import { DrugProgramTerms } from '../pages/programs/DrugProgramTerms';
import RenewalStatusPage from '../pages/renewal-status/RenewalStatusPage';
import { SupportPage } from '../pages/support/SupportPage';
import { SwitchConfirmationPage } from '../pages/switch-prescription/SwitchConfirmationPage';
import { SwitchPrescriptionProvider } from '../pages/switch-prescription/SwitchPrescriptionProvider';
import { SwitchPrescriptionRoot } from '../pages/switch-prescription/SwitchPrescriptionRoot';
import { SwitchVerificationScreen } from '../pages/switch-prescription/SwitchVerificationScreen';
import UnauthHomePage from '../pages/unauth/UnauthHomePage';
import { VerifyPatientPage } from '../pages/verify-prescription/VerifyPatientPage';
import { VerifyPrescriptionPage } from '../pages/verify-prescription/VerifyPrescriptionPage';
import { VerifySwitchPrescriptionPage } from '../pages/verify-switch-prescription/VerifySwitchPrescriptionPage';
import ProviderDetails from '../provider/ProviderDetails';

import { AuthedProviders } from './AuthedProviders';
import RootErrorRoute from './RootErrorRoute';
import { routes } from './routes';

const router = createBrowserRouter([
    {
        path: '/',
        element: <AppRoot />,
        errorElement: <RootErrorRoute p="md" />,
        children: [
            {
                index: true,
                element: <UnauthHomePage />,
            },
            {
                path: routes.loginCallback.root,
                element: <LoginCallback />,
            },
            {
                path: routes.logout,
                element: <LogoutRoute returnUrl={routes.root} />,
            },
            {
                path: routes.error,
                element: <RootErrorRoute />,
            },
            {
                path: routes.notFound,
                element: <RootErrorRoute />,
            },
            {
                path: routes.forbidden,
                element: <RootErrorRoute />,
            },
            {
                path: routes.support,
                element: <SupportPage />,
            },
            {
                element: <AuthedProviders />,
                children: [
                    {
                        path: routes.home,
                        element: (
                            <AuthorizedRoute>
                                <HomePage />
                            </AuthorizedRoute>
                        ),
                    },
                    {
                        path: routes.programs.root,
                        children: [
                            {
                                path: routes.programs.drugKey.root,
                                children: [
                                    {
                                        path: routes.programs.drugKey.terms
                                            .root,
                                        children: [
                                            {
                                                path: routes.programs.drugKey
                                                    .terms.pathway,
                                                element: (
                                                    <AuthorizedRoute>
                                                        <DrugProgramTerms />
                                                    </AuthorizedRoute>
                                                ),
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: routes.fill.root,
                        children: [
                            {
                                path: routes.fill.drxId.root,
                                element: (
                                    <AuthorizedRoute>
                                        <EnhancedFillFlagCheck>
                                            <FillPrescriptionProvider>
                                                <AutoDetectPathway />
                                                <Outlet />
                                            </FillPrescriptionProvider>
                                        </EnhancedFillFlagCheck>
                                    </AuthorizedRoute>
                                ),
                                children: [
                                    {
                                        index: true,
                                        id: 'attestation',
                                        element: <AttestationScreen />,
                                    },
                                    {
                                        id: 'coverage-select',
                                        path: routes.fill.drxId.coverageSelect,
                                        element: <CoverageSelectScreen />,
                                    },
                                    {
                                        id: 'coverage-sub-select',
                                        path: routes.fill.drxId
                                            .coverageSubSelect,
                                        element: <CoverageSubSelectScreen />,
                                    },
                                    {
                                        id: 'on-hold',
                                        path: routes.fill.drxId.onHold,
                                        element: <OnHoldScreen />,
                                    },
                                    {
                                        id: 'switch',
                                        path: routes.fill.drxId.switch,
                                        element: <SwitchPrescriptionScreen />,
                                    },
                                    {
                                        id: 'path-select',
                                        path: routes.fill.drxId.pathSelect,
                                        element: <PathSelectionScreen />,
                                    },
                                    {
                                        path: routes.fill.drxId.pharmacy.root,
                                        element: <Outlet />,
                                        children: [
                                            {
                                                index: true,
                                                id: 'pharmacy-select',
                                                element: (
                                                    <SelectPharmacyScreen />
                                                ),
                                            },
                                            {
                                                id: 'pharmacy-preview',
                                                path: routes.fill.drxId.pharmacy
                                                    .offer,
                                                element: (
                                                    <PharmacyPreviewScreen />
                                                ),
                                            },
                                        ],
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: routes.order.root,
                        children: [
                            {
                                path: routes.order.prescriberOrderId.root,
                                element: (
                                    <AuthorizedRoute>
                                        <PrescriberOrderProvider>
                                            <Outlet />
                                        </PrescriberOrderProvider>
                                    </AuthorizedRoute>
                                ),
                                children: [
                                    {
                                        index: true,
                                        element: <PrescriptionPage />,
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .switch.root,
                                        element: (
                                            <SwitchPrescriptionProvider>
                                                <Outlet />
                                            </SwitchPrescriptionProvider>
                                        ),
                                        children: [
                                            {
                                                index: true,
                                                element: (
                                                    <SwitchPrescriptionRoot />
                                                ),
                                            },
                                            {
                                                path: routes.order
                                                    .prescriberOrderId.switch
                                                    .verify,
                                                element: (
                                                    <VerifySwitchPrescriptionPage />
                                                ),
                                            },
                                            {
                                                path: routes.order
                                                    .prescriberOrderId.switch
                                                    .target.root,
                                                element: <Outlet />,
                                                children: [
                                                    {
                                                        index: true,
                                                        element: (
                                                            <SwitchVerificationScreen />
                                                        ),
                                                    },
                                                    {
                                                        path: routes.order
                                                            .prescriberOrderId
                                                            .switch.target
                                                            .confirmation,
                                                        element: (
                                                            <SwitchConfirmationPage />
                                                        ),
                                                    },
                                                ],
                                            },
                                        ],
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .confirmation,
                                        element: <OrderConfirmationPage />,
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .renewalStatus,
                                        element: <RenewalStatusPage />,
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .details,
                                        element: <OrderDetailsPage />,
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .summary,
                                        element: <OrderSummaryPage />,
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .pharmacy.root,
                                        element: <Outlet />,
                                        children: [
                                            {
                                                path: routes.order
                                                    .prescriberOrderId.pharmacy
                                                    .providerId,
                                                element: <ProviderDetails />,
                                            },
                                        ],
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .verify,
                                        element: <VerifyPrescriptionPage />,
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .verifyPatient,
                                        element: <VerifyPatientPage />,
                                    },
                                    {
                                        path: routes.order.prescriberOrderId
                                            .otherOptions,
                                        element: <OtherOptionsPage />,
                                    },
                                ],
                            },
                        ],
                    },
                    {
                        path: routes.cabinet.root,
                        element: <AuthorizedOutlet />,
                        children: [
                            {
                                index: true,
                                element: <MedicineCabinetPage />,
                            },
                            {
                                path: routes.cabinet.history,
                                element: <MedicineCabinetHistoryPage />,
                            },
                        ],
                    },
                    {
                        path: routes.profile.root,
                        element: <AuthorizedOutlet />,
                        children: [
                            {
                                index: true,
                                element: <ProfilePage />,
                            },
                            {
                                path: routes.profile.consents.root,
                                element: <Outlet />,
                                children: [
                                    {
                                        index: true,
                                        element: <ProfileConsentsPage />,
                                    },
                                    {
                                        path: routes.profile.consents.consentId,
                                        element: <ProfileConsentDetailPage />,
                                    },
                                ],
                            },
                        ],
                    },
                ],
            },
        ],
    },
]);

// Maintenance router with limited routes during maintenance
export const maintenanceRouter = createBrowserRouter([
    {
        path: '/',
        element: <AppRoot />,
        errorElement: <Maintenance />,
        children: [
            {
                index: true,
                element: <UnauthHomePage />,
            },
            {
                path: routes.loginCallback.root,
                element: <LoginCallback />,
            },
            {
                path: routes.logout,
                element: <LogoutRoute returnUrl={routes.root} />,
            },
            {
                path: routes.support,
                element: <SupportPage />,
            },
        ],
    },
    {
        path: '*',
        element: <Maintenance />,
    },
]);

export default router;
